import React, { useEffect, useRef } from 'react';
import useFetch from 'use-http';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import { Alert, Autocomplete, Box, Paper, Stack, styled, TextField, Typography, CircularProgress } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MaxWidthText } from '@allenai/varnish2';
import { InlineFiguresCarouselWidget, InlineFigureWidget } from '../components/InlineFigureWidget';
import { FeedbackWidget } from '../components/FeedbackWidget';
import { Thread, Message, MessageFeedback } from '../types/Thread';
import { createThread, addMessageToThread, generateMessageId, generateThreadTitle } from '../utils/threadStorage';
import * as ThreadsAPI from '../api/Threads';
import { searchPapersByTitle, PaperSuggestion, validatePaperTitle } from '../api/PaperSearch';
import { buildApiHeaders } from '../api/apiUtils';

/**
 * Helper function to detect if the input looks like a Semantic Scholar URL
 */
const looksLikeSemanticScholarUrl = (input: string): boolean => {
    if (!input) return false;
    const trimmed = input.trim();
    // Check if it starts with http/https or contains semanticscholar.org
    return trimmed.startsWith('http://') || 
           trimmed.startsWith('https://') || 
           trimmed.includes('semanticscholar.org');
};

/**
 * This type defines the different values that will be accepted from the form's inputs.
 * The names of the keys (Eg. 'question', 'choices') must match
 * the HTML input's declared 'name' attribute.
 *
 * Eg:
 * ``` <Form.Item label="Question:" name="question"> ... </Form.Item>
 *
 * In this example, we aligned the
 * form's 'name' attributes with the keys of the 'Query' type that is used to query the API.
 */
type FormValue = {
    question?: string;
    paper_title?: string;
};

interface HomeProps {
    activeThread: Thread | null;
    onThreadsUpdate: () => void;
    onThreadSelect: (threadId: string) => void;
    onUpdateThread: (thread: Thread) => void;
    focusPaperTitleInput?: boolean;
}

export const Home = ({ activeThread, onThreadsUpdate, onThreadSelect, onUpdateThread, focusPaperTitleInput }: HomeProps) => {
    /**
     * These are react hooks. You can read more about hooks here:
     * @see https://legacy.reactjs.org/docs/hooks-intro.html
     */
    // no navigation needed; we call the API directly on form submit

    /**
     * This fetches an answer by hitting /api/solve, which is defined in
     * api.py. The provided query is serialized and sent across the wire as JSON.
     *
     * usefetch is a library for making HTTP requests.
     * You can find more about it here:
     * @see https://github.com/ava/use-http
     *
     * /api/solve takes a `Query` and returns an `Answer`:
     */
    const paperqaFetch = useFetch<any>('/api/paperqa', {
        interceptors: {
            request: async ({ options }) => {
                const headers = await buildApiHeaders();
                options.headers = {
                    ...options.headers,
                    ...headers
                };
                return options;
            }
        }
    });
    const {
        post: paperqaPost,
        loading: paperqaLoading,
        error: paperqaError,
    } = paperqaFetch;

    // Fetch username from dedicated API endpoint
    const {
        get: getUsernameGet,
    } = useFetch<any>('/api/username', {
        interceptors: {
            request: async ({ options }) => {
                const headers = await buildApiHeaders();
                options.headers = {
                    ...options.headers,
                    ...headers
                };
                return options;
            }
        }
    });

    // API hook for resolving corpus_id to paper title
    const {
        post: resolveCorpusIdPost,
    } = useFetch<any>('/api/resolve-corpus-id', {
        interceptors: {
            request: async ({ options }) => {
                const headers = await buildApiHeaders();
                options.headers = {
                    ...options.headers,
                    ...headers
                };
                return options;
            }
        }
    });

    // State to store the username
    const [username, setUsername] = React.useState<string | null>(null);

    // Fetch username on component mount
    useEffect(() => {
        const fetchUsername = async () => {
            try {
                const res = await getUsernameGet();
                const fetchedUsername = res?.username || null;
                setUsername(fetchedUsername);
            } catch (err) {
                console.error('Error fetching username:', err);
            }
        };
        fetchUsername();
    }, [getUsernameGet]);

    // Read corpus_id from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const corpusIdFromUrl = urlParams.get('corpus_id');

    const formContext = useForm<FormValue>({
        defaultValues: {},
    });

    // Watch form values to enable/disable submit button
    const paperTitle = formContext.watch('paper_title');
    const isSubmitDisabled = !paperTitle;

    // State for paper search autocomplete
    const [paperSearchQuery, setPaperSearchQuery] = React.useState<string>('');
    const [paperSuggestions, setPaperSuggestions] = React.useState<PaperSuggestion[]>([]);
    const [paperSearchLoading, setPaperSearchLoading] = React.useState<boolean>(false);
    // Track selected paper suggestion (not currently used for submission but available for future optimization)
    const [_selectedPaper, setSelectedPaper] = React.useState<PaperSuggestion | null>(null);
    // Screen reader announcement for search results
    const [searchAnnouncement, setSearchAnnouncement] = React.useState<string>('');

    // State for paper title validation
    const [lastValidatedTitle, setLastValidatedTitle] = React.useState<string>('');
    const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [validationError, setValidationError] = React.useState<string | null>(null);

    // State for follow-up conversation
    // Conversation history loaded from activeThread
    const [conversation, setConversation] = React.useState<Message[]>([]);
    const [conversationLength, setConversationLength] = React.useState<number>(0);

    const [followUpQuestion, setFollowUpQuestion] = React.useState<string>('');
    // Store the active corpus_id (resolved from backend) for follow-up questions
    const [activeCorpusId, setActiveCorpusId] = React.useState<string | undefined>(undefined);
    // Track paper title from URL to prevent it from being cleared
    const [urlResolvedPaperTitle, setUrlResolvedPaperTitle] = React.useState<string | undefined>(undefined);
    // Store resolved paper title for active thread (when not available on thread object)
    const [resolvedPaperTitle, setResolvedPaperTitle] = React.useState<string | undefined>(undefined);
    // Track whether to show the error (cleared when switching to new thread)
    const [showError, setShowError] = React.useState<boolean>(true);
    // Track previous thread ID to detect thread changes
    const prevThreadIdRef = useRef<string | null>(null);

    // Resolve corpus_id from URL to paper title on component mount
    useEffect(() => {
        const resolveCorpusId = async () => {
            if (corpusIdFromUrl) {
                try {
                    const res = await resolveCorpusIdPost({ corpus_id: corpusIdFromUrl });
                    if (res?.paper_title) {
                        setUrlResolvedPaperTitle(res.paper_title);
                        formContext.setValue('paper_title', res.paper_title);
                        setPaperSearchQuery(res.paper_title);
                        // Store the corpus_id for later use
                        setActiveCorpusId(corpusIdFromUrl);
                        // No need to call onThreadSelect - App.tsx already handles this
                    }
                } catch (err) {
                    console.error('Error resolving corpus_id:', err);
                }
            }
        };
        resolveCorpusId();
        // Only run once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced paper title search effect
    useEffect(() => {
        // Don't search if query is empty or less than 3 characters
        if (!paperSearchQuery || paperSearchQuery.trim().length < 3) {
            setPaperSuggestions([]);
            setSearchAnnouncement('');
            return;
        }

        // Don't search if the input looks like a URL
        if (looksLikeSemanticScholarUrl(paperSearchQuery)) {
            setPaperSuggestions([]);
            setSearchAnnouncement('');
            return;
        }

        setPaperSearchLoading(true);
        setSearchAnnouncement('Searching for papers...');
        
        // Debounce: wait 300ms after user stops typing before searching
        const timeoutId = setTimeout(async () => {
            try {
                const suggestions = await searchPapersByTitle(paperSearchQuery, 10);
                setPaperSuggestions(suggestions);
                
                // Announce results to screen readers
                if (suggestions.length === 0) {
                    setSearchAnnouncement('No paper suggestions found. You can still enter a custom title or URL.');
                } else if (suggestions.length === 1) {
                    setSearchAnnouncement('1 paper suggestion found. Use arrow keys to navigate.');
                } else {
                    setSearchAnnouncement(`${suggestions.length} paper suggestions found. Use arrow keys to navigate.`);
                }
            } catch (error) {
                console.error('Error searching papers:', error);
                setPaperSuggestions([]);
                setSearchAnnouncement('Error searching for papers. Please try again.');
            } finally {
                setPaperSearchLoading(false);
            }
        }, 300);

        // Cleanup: cancel the timeout if user keeps typing
        return () => {
            clearTimeout(timeoutId);
            setPaperSearchLoading(false);
        };
    }, [paperSearchQuery]);

    // Load conversation from activeThread whenever it changes
    useEffect(() => {
        const currentThreadId = activeThread?.id || null;
        const previousThreadId = prevThreadIdRef.current;
        const hasThreadChanged = previousThreadId !== currentThreadId;
        
        if (activeThread) {
            setConversation(activeThread.messages);
            setActiveCorpusId(activeThread.corpusId);
            // Only clear error if we've switched from one existing thread to another
            // Don't clear if switching from null (new submission) to a thread
            if (hasThreadChanged && previousThreadId !== null) {
                setShowError(false);
                setValidationError(null);
            }
        } else {
            setConversation([]);
            // Always clear fields when switching to "New Thread"
            // But preserve URL-resolved paper title if it exists
            if (!urlResolvedPaperTitle) {
                setActiveCorpusId(undefined);
                formContext.setValue('paper_title', '');
                setPaperSearchQuery('');
                setSelectedPaper(null);
            }
            formContext.setValue('question', '');
            
            // Clear any error when switching to new thread (from an existing thread)
            if (hasThreadChanged && previousThreadId !== null) {
                setShowError(false);
                setValidationError(null);
            }
        }
        
        // Update the ref to track the current thread ID
        prevThreadIdRef.current = currentThreadId;
    }, [activeThread, formContext, urlResolvedPaperTitle]);

    // Show error when a new error occurs
    useEffect(() => {
        if (paperqaError) {
            setShowError(true);
        }
    }, [paperqaError]);

    // Resolve paper title for active thread if not already available
    useEffect(() => {
        const resolvePaperTitle = async () => {
            if (activeThread && !activeThread.paperTitle && activeThread.corpusId) {
                try {
                    const res = await resolveCorpusIdPost({ corpus_id: activeThread.corpusId });
                    if (res?.paper_title) {
                        setResolvedPaperTitle(res.paper_title);
                    }
                } catch (err) {
                    console.error('Error resolving paper title for thread:', err);
                }
            } else {
                // Clear resolved paper title if we switch threads or thread has paper title
                setResolvedPaperTitle(undefined);
            }
        };
        resolvePaperTitle();
    }, [activeThread, resolveCorpusIdPost]);

    // Accessibility and scrolling
    const endOfMessagesRef = useRef<HTMLDivElement | null>(null);
    const latestAnswerRef = useRef<HTMLDivElement | null>(null);
    const paperTitleInputRef = useRef<HTMLInputElement | null>(null);
    const shouldFocusAnswer = useRef<boolean>(false);
    const [loadingAnnouncement, setLoadingAnnouncement] = React.useState<string>('');
    
    // Track loading state changes for screen reader announcements
    useEffect(() => {
        const isLoading = paperqaLoading;
        if (isLoading) {
            setLoadingAnnouncement('Loading answer, please wait...');
            shouldFocusAnswer.current = true; // Set flag to focus when answer loads
        } else {
            setLoadingAnnouncement(''); // Clear loading when done
        }
    }, [paperqaLoading]);

    // Cleanup validation timeout on unmount
    useEffect(() => {
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, []);

    // Focus paper title input when requested (e.g., when New Thread button is clicked)
    useEffect(() => {
        if (!activeThread && focusPaperTitleInput) {
            // Small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                paperTitleInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [activeThread, focusPaperTitleInput]);

    // On conversation update, scroll to bottom and focus the latest answer
    useEffect(() => {
        // Only scroll if a new message was added (not just feedback updated)
        const hasNewMessage = conversation.length > conversationLength;
        
        if (hasNewMessage && conversation.length > 0) {
            // Update the conversation length tracker
            setConversationLength(conversation.length);

            // Scroll to the answer
            setTimeout(() => {
                endOfMessagesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
        }
        
        // Separately check if we should focus (happens after loading completes)
        if (shouldFocusAnswer.current && conversation.length > 0 && latestAnswerRef.current) {
            setTimeout(() => {
                if (latestAnswerRef.current) {
                    latestAnswerRef.current.focus();
                    shouldFocusAnswer.current = false; // Reset flag
                }
            }, 500);
        }
        
        return undefined;
    }, [conversation, conversationLength]);

    /**
     * Submits a call to the API for a given query as defined in the URL parameters
     */
    // We no longer auto-fetch based on URL. The UI will call the PaperQA API on form submit.

    /**
     * This handler is invoked when the form is submitted, which occurs when
     * the user clicks the submit button or when the user clicks input while
     * the button and/or a form element is selected.
     *
     * We use this instead of a onClick button on a button as it matches the
     * traditional form experience that end users expect.
     *
     * @see https://reactjs.org/docs/forms.html
     */
    const handleSubmit = async (values: FormValue) => {
        // POST the question and paper_title to the PaperQA backend
        try {
            const requestBody: any = {
                question: values.question,
                with_figures: true,
                use_synthetic_snippets: true,
            };
            
            // Send paper_title to the backend (backend will resolve to corpus_id)
            if (values.paper_title) {
                requestBody.paper_title = values.paper_title;
            }
            
            const res = await paperqaPost(requestBody);
            
            // Store the corpus_id and paper_title returned from the backend
            const resolvedCorpusId = res?.corpusId?.toString();
            const resolvedPaperTitle = res?.paperTitle;
            
            if (resolvedCorpusId) {
                setActiveCorpusId(resolvedCorpusId);
            }
            
            // Clear the URL-resolved paper title flag since we've now submitted
            setUrlResolvedPaperTitle(undefined);
            
            // Create a new thread for this paper
            const threadTitle = generateThreadTitle(values.question || 'New Thread');
            const newThread = await createThread(
                threadTitle,
                resolvedCorpusId,
                resolvedPaperTitle || values.paper_title,  // Use resolved paper title from API, fallback to input
                username || undefined  // Pass username from state
            );
            
            // Create the first message
            const message: Message = {
                id: generateMessageId(),
                question: values.question || '',
                answer: res?.answer ?? JSON.stringify(res),
                figures: res?.figures || {},
                timestamp: Date.now(),
            };
            
            // Add message to thread
            const updatedThread = await addMessageToThread(newThread.id, message);
            
            if (updatedThread) {
                // Update the thread in place with the full details (including messages)
                onUpdateThread(updatedThread);
            }
            setFollowUpQuestion('');
            
            // Select this thread
            onThreadSelect(newThread.id);
            
            // Refresh threads list to get updated metadata
            onThreadsUpdate();
        } catch (err) {
            // errors are surfaced via paperqaError from useFetch
            console.error(err);
        }
    };

    // Handler for follow-up question submission
    const handleFollowUpSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!activeThread) {
            console.error('No active thread');
            return;
        }
        
        try {
            // Use unified paperqa endpoint with thread_id for multi-turn queries
            const res = await paperqaPost({
                thread_id: activeThread.id,
                question: followUpQuestion,
            });
            
            // Create a new message
            const message: Message = {
                id: generateMessageId(),
                question: followUpQuestion,
                answer: res?.answer ?? JSON.stringify(res),
                figures: res?.figures || {},
                timestamp: Date.now(),
            };
            
            // Add message to thread
            const updatedThread = await addMessageToThread(activeThread.id, message);
            
            if (updatedThread) {
                // Update the thread in place with all messages
                onUpdateThread(updatedThread);
            }
            setFollowUpQuestion('');
            
            // Refresh threads list to update timestamps in sidebar
            onThreadsUpdate();
        } catch (err: any) {
            console.log(err?.message || 'Sorry, something went wrong.');
        }
    };

    // Handler for feedback submission
    const handleFeedbackSubmit = async (messageId: string, feedback: MessageFeedback) => {
        if (!activeThread) {
            console.error('No active thread');
            return;
        }

        try {
            // Call backend API to persist feedback
            await ThreadsAPI.updateMessageFeedback(
                activeThread.id,
                messageId,
                feedback.rating,
                feedback.comment
            );

            // Update the message with feedback in the local conversation state
            const updatedMessages = conversation.map(msg => 
                msg.id === messageId ? { ...msg, feedback } : msg
            );
            setConversation(updatedMessages);

            // Update the thread with the new messages
            const updatedThread = {
                ...activeThread,
                messages: updatedMessages,
                updatedAt: Date.now(),
            };
            onUpdateThread(updatedThread);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            // You could add error handling UI here if needed
        }
    };

    // Handler for paper title validation (when user moves to another field)
    const handlePaperTitleValidation = async (paperTitle: string) => {
        if (!paperTitle || paperTitle.trim().length === 0) {
            setValidationError(null);
            return;
        }

        // Don't validate if we already validated this exact title
        if (paperTitle.trim() === lastValidatedTitle) {
            console.log('Skipping validation - already validated this title');
            return;
        }

        // Clear any pending validation timeout
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        // Clear previous validation error
        setValidationError(null);

        // Debounce: wait 1 second before validating to avoid rapid API calls
        validationTimeoutRef.current = setTimeout(async () => {
            try {
                const validationResult = await validatePaperTitle(paperTitle);
                
                // Check if paper is searchable
                if (validationResult.searchable === false) {
                    setValidationError("Unfortunately, we do not have access to the full text and figures of this paper. Please try another paper.");
                } else {
                    setValidationError(null);
                }
                
                // Mark this title as validated to prevent duplicate calls
                setLastValidatedTitle(paperTitle.trim());
            } catch (error) {
                console.error('Error validating paper title:', error);
                // Show error for invalid paper name
                setValidationError("This is an invalid paper name, please try another.");
                setLastValidatedTitle(''); // Allow retry
            }
        }, 1000);
    };

    /**
     * The render method defines what's rendered. When writing yours keep in
     * mind that you should try to make it a "pure" function of the component's
     * props and state.  In other words, the rendered output should be expressed
     * as a function of the component properties and state.
     *
     * React executes render whenever a component's properties and/or state is
     * updated. The output is then compared with what's rendered and the
     * required updates are made. This is to ensure that rerenders make as few
     * changes to the document as possible -- which can be an expensive process
     * and lead to slow interfaces.
     */
    return (
        <ChatContainer>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: activeThread ? 'center' : 'center' }}>
                <Box sx={{ textAlign: activeThread ? 'center' : 'left', maxWidth: '65ch', width: '100%' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: '#0FCB8C', fontSize: '2.5rem' }}>Paper+Figure QA</Typography>
                    {activeThread ? (
                        <MaxWidthText as="p" style={{ color: '#FFFFFF', paddingTop: 0 }}>
                            {(() => {
                                // Get paper title from thread or resolved state
                                const paperTitle = activeThread.paperTitle || resolvedPaperTitle;
                                const corpusId = activeThread.corpusId;
                                
                                // If we have both paper title and corpus ID, make it a link
                                if (paperTitle && corpusId) {
                                    return (
                                        <a
                                            href={`https://www.semanticscholar.org/p/${corpusId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ 
                                                color: '#ffffffff', 
                                                textDecoration: 'underline',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {paperTitle}
                                        </a>
                                    );
                                }
                                // If we have paper title but no corpus ID, just show the title
                                if (paperTitle) {
                                    return paperTitle;
                                }
                                // Fallback if no paper title available
                                return activeThread.title;
                            })()}
                        </MaxWidthText>
                    ) : (
                        <>
                            <Typography variant="body1" sx={{ color: '#FFFFFF', paddingTop: 0 }}>
                                Enter a paper title or{' '}
                                <a 
                                    href="https://www.semanticscholar.org/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#0FCB8C', textDecoration: 'underline' }}
                                >
                                    Semantic Scholar
                                </a>
                                {' '}URL and a question below to start a new thread. You have access to all paper content, including figures, tables, and text.
                            </Typography>
                            <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 600, paddingTop: 3 }}>
                                You can ask Paper+Figure QA:
                            </Typography>
                            <Typography component="ul" sx={{ color: '#FFFFFF', paddingTop: 1, listStylePosition: 'inside', pl: 0 }}>
                                <li>General reasoning questions about the paper</li>
                                <li>Questions that require multiple figures to answer</li>
                                <li>Insights from a specific figure or table</li>
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#FFFFFF', paddingTop: 2, display: 'block' }}>
                                A project from{' '}
                                <a 
                                    href="https://allenai.org/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#0FCB8C', textDecoration: 'underline' }}
                                >
                                    Ai2
                                </a>
                                . Paper+Figure QA can make mistakes.
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>
            <ChatContent id="chat-scroll-container">
                {paperqaError && showError ? (
                    <Alert severity="error">
                        {'Sorry, something went wrong. Please try a new thread using the + button.'}
                    </Alert>
                ) : null}
                {/* Conversation as chat bubbles */}
                <Stack spacing={1}>
                    {conversation.map((item, idx) => {
                        const figuresObj = item.figures;
                        return (
                            <React.Fragment key={idx}>
                                {/* Question bubble (right aligned) */}
                                <RightRow>
                                    <BubblePaper elevation={2} sx={{ bgcolor: '#0A3235', color: '#FFFFFF' }}>
                                        <div
                                            tabIndex={0}
                                            role="article"
                                            aria-label={`Question ${idx + 1}`}
                                        >
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#FFFFFF' }}>{item.question}</Typography>
                                        </div>
                                    </BubblePaper>
                                </RightRow>
                                {/* Answer bubble (left aligned) */}
                                <LeftRow>
                                    <BubblePaper
                                        elevation={2}
                                        sx={{ bgcolor: '#0A3235', color: '#FFFFFF' }}
                                    >
                                        <div
                                            // Make all answers focusable via div wrapper
                                            tabIndex={0}
                                            ref={idx === conversation.length - 1 ? latestAnswerRef : undefined}
                                            role="article"
                                            aria-label={`Answer ${idx + 1}`}
                                        >
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#FFFFFF' }}>
                                                {item.answer ? (typeof item.answer === 'string' ? item.answer : JSON.stringify(item.answer, null, 2)) : 'No answer received. See console for API response.'}
                                            </Typography>
                                            {/* Figures carousel */}
                                            {figuresObj && typeof figuresObj === 'object' && Object.keys(figuresObj).length > 0 && (
                                                <InlineFiguresCarouselWidget>
                                                    {Object.entries(figuresObj).map(([figName, figData]: [string, any]) => (
                                                        <InlineFigureWidget 
                                                            key={figName}
                                                            name={figName}
                                                            url={(figData as any).url}
                                                            caption={(figData as any).caption}
                                                        />
                                                    ))}
                                                </InlineFiguresCarouselWidget>
                                            )}
                                            {/* Feedback Widget */}
                                            <FeedbackWidget
                                                messageId={item.id}
                                                existingFeedback={item.feedback}
                                                onFeedbackSubmit={handleFeedbackSubmit}
                                            />
                                        </div>
                                    </BubblePaper>
                                </LeftRow>
                                {/* Figures are now inside the answer bubble */}
                            </React.Fragment>
                        );
                    })}
                    {/* Loading spinner - shown when waiting for answer */}
                    {paperqaLoading && (
                        <LoadingContainer>
                            <CircularProgress 
                                size={60} 
                                sx={{ color: '#0FCB8C' }}
                                aria-label="Loading answer"
                            />
                            <Typography variant="body1" sx={{ color: '#0FCB8C', mt: 2 }}>
                                Loading...
                            </Typography>
                        </LoadingContainer>
                    )}
                    {/* Sentinel for auto-scroll */}
                    <div ref={endOfMessagesRef} />
                </Stack>
            </ChatContent>

            {/* Bottom composer bar */}
            <ComposerBar>
                {!activeThread ? (
                    // Initial composer: Paper Title, Question, Submit
                    <Box sx={{ maxWidth: 800, width: '100%', py: 2, px: 3 }}>
                        <FormContainer
                            formContext={formContext}
                            onSuccess={(values: any) => {
                                handleSubmit(values);
                            }}>
                            <Stack direction="column" spacing={1.5}>
                                {/* Validation Error Alert */}
                                {validationError && (
                                    <Alert 
                                        severity="error"
                                        onClose={() => setValidationError(null)}
                                        sx={{
                                            bgcolor: '#4A1A1A',
                                            color: '#FFFFFF',
                                            '& .MuiAlert-icon': {
                                                color: '#FF6B6B'
                                            },
                                            border: '1px solid #FF6B6B55'
                                        }}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {validationError}
                                    </Alert>
                                )}
                                <Autocomplete
                                    freeSolo
                                    options={paperSuggestions}
                                    filterOptions={(x) => x} // Disable client-side filtering since we filter on the server
                                    // Open dropdown above the input (upward)
                                    componentsProps={{
                                        popper: {
                                            placement: 'top-start',
                                            modifiers: [
                                                {
                                                    name: 'flip',
                                                    enabled: false, // Disable auto-flipping to keep it always on top
                                                },
                                            ],
                                        },
                                    }}
                                    getOptionLabel={(option) => {
                                        // option can be a string (when freeSolo input) or PaperSuggestion object
                                        if (typeof option === 'string') {
                                            return option;
                                        }
                                        return option.title;
                                    }}
                                    // Accessibility: announce option to screen readers
                                    getOptionKey={(option) => {
                                        if (typeof option === 'string') {
                                            return option;
                                        }
                                        return option.paperId || option.title;
                                    }}
                                    renderOption={(props, option) => {
                                        // Render each suggestion with title, authors, and year
                                        const { key, ...otherProps } = props as any;
                                        
                                        // Build accessible description
                                        const authorText = option.authors && option.authors.length > 0
                                            ? option.authors.slice(0, 3).map((a: any) => a.name).join(', ') + (option.authors.length > 3 ? ', and others' : '')
                                            : '';
                                        const metaText = [authorText, option.year, option.venue].filter(Boolean).join(' • ');
                                        
                                        return (
                                            <li 
                                                key={key} 
                                                {...otherProps}
                                                aria-label={`${option.title}${metaText ? `. ${metaText}` : ''}`}
                                            >
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                                                        {option.title}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#A0A0A0' }}>
                                                        {option.authors && option.authors.length > 0 && (
                                                            <>
                                                                {option.authors.slice(0, 3).map((a: any) => a.name).join(', ')}
                                                                {option.authors.length > 3 && ', ...'}
                                                            </>
                                                        )}
                                                        {option.year && ` • ${option.year}`}
                                                        {option.venue && ` • ${option.venue}`}
                                                    </Typography>
                                                </Box>
                                            </li>
                                        );
                                    }}
                                    inputValue={paperSearchQuery}
                                    onInputChange={(_, newInputValue) => {
                                        setPaperSearchQuery(newInputValue);
                                        // Update form value to keep react-hook-form in sync
                                        formContext.setValue('paper_title', newInputValue);
                                        // Clear validation error when user starts typing a new title
                                        if (validationError) {
                                            setValidationError(null);
                                        }
                                    }}
                                    onChange={(_, newValue) => {
                                        // When user selects a suggestion
                                        if (typeof newValue === 'object' && newValue !== null) {
                                            setSelectedPaper(newValue);
                                            setPaperSearchQuery(newValue.title);
                                            formContext.setValue('paper_title', newValue.title);
                                            // Announce selection to screen readers
                                            setSearchAnnouncement(`Selected: ${newValue.title}`);
                                            // Clear validation error when selecting a new paper
                                            if (validationError) {
                                                setValidationError(null);
                                            }
                                        } else if (typeof newValue === 'string') {
                                            // User typed custom text (freeSolo)
                                            setSelectedPaper(null);
                                            setPaperSearchQuery(newValue);
                                            formContext.setValue('paper_title', newValue);
                                            // Clear validation error when typing new text
                                            if (validationError) {
                                                setValidationError(null);
                                            }
                                        }
                                    }}
                                    loading={paperSearchLoading}
                                    // Accessibility props
                                    openText="Open paper suggestions"
                                    closeText="Close paper suggestions"
                                    clearText="Clear paper title"
                                    loadingText="Searching for papers..."
                                    noOptionsText={paperSearchQuery.length < 3 
                                        ? "Type at least 3 characters to search" 
                                        : "No papers found. You can still enter a custom title or URL."}
                                    renderInput={(params) => {
                                        // Combine the autocomplete ref with our custom ref
                                        const inputRefCallback = (element: HTMLInputElement | null) => {
                                            if (params.InputProps.ref) {
                                                if (typeof params.InputProps.ref === 'function') {
                                                    params.InputProps.ref(element);
                                                } else {
                                                    (params.InputProps.ref as any).current = element;
                                                }
                                            }
                                            if (paperTitleInputRef) {
                                                paperTitleInputRef.current = element;
                                            }
                                        };
                                        
                                        return (
                                            <TextField
                                                {...params as any}
                                                label="Paper Title or Semantic Scholar URL"
                                                required
                                                autoComplete="off"
                                                // Accessibility
                                                aria-describedby="paper-search-helper-text"
                                                helperText={
                                                    <span id="paper-search-helper-text">
                                                        Starting typing to see title suggestions. You can also input a full Semantic Scholar URL or full paper title.
                                                    </span>
                                                }
                                                onBlur={(e) => {
                                                    // Validate paper title when user moves to another field
                                                    handlePaperTitleValidation(e.target.value);
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ref: inputRefCallback,
                                                    endAdornment: (
                                                        <>
                                                            {paperSearchLoading ? (
                                                                <CircularProgress 
                                                                    color="inherit" 
                                                                    size={20} 
                                                                    aria-label="Searching for papers"
                                                                />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                sx={{ 
                                                    bgcolor: '#0A3235', 
                                                    '& .MuiInputBase-input': { color: '#FFFFFF' }, 
                                                    '& .MuiInputLabel-root': { color: '#0FCB8C' }, 
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#0FCB8C55' },
                                                    '& .MuiFormHelperText-root': { color: '#A0A0A0' },
                                                    '& .MuiAutocomplete-endAdornment .MuiCircularProgress-root': { 
                                                        color: '#0FCB8C' 
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                    sx={{
                                        // Style the dropdown/popper
                                        '& .MuiAutocomplete-popper': {
                                            '& .MuiPaper-root': {
                                                bgcolor: '#0A3235',
                                                border: '1px solid #0FCB8C55',
                                            },
                                            '& .MuiAutocomplete-listbox': {
                                                bgcolor: '#0A3235',
                                                '& .MuiAutocomplete-option': {
                                                    '&[aria-selected="true"]': {
                                                        bgcolor: '#0FCB8C22',
                                                    },
                                                    '&.Mui-focused': {
                                                        bgcolor: '#0FCB8C33',
                                                    },
                                                    '&:hover': {
                                                        bgcolor: '#0FCB8C22',
                                                    },
                                                },
                                            },
                                            '& .MuiAutocomplete-noOptions': {
                                                color: '#A0A0A0',
                                            },
                                            '& .MuiAutocomplete-loading': {
                                                color: '#A0A0A0',
                                            },
                                        },
                                    }}
                                />
                                <TextFieldElement
                                    name="question"
                                    fullWidth
                                    label="Question"
                                    multiline
                                    placeholder="Enter a question"
                                    required
                                    rows={3}
                                    rules={{
                                        required: 'Question is required',
                                        minLength: { value: 1, message: 'Question must not be empty' },
                                    }}
                                    helperText='Ask about text, figures, or tables (e.g., "Compare the results in Figure 1 and 3")'
                                    sx={{ bgcolor: '#0A3235', '& .MuiInputBase-input': { color: '#FFFFFF' }, '& .MuiInputLabel-root': { color: '#0FCB8C' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#0FCB8C55' }, '& .MuiFormHelperText-root': { color: '#A0A0A0' } }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Button type={'submit'} variant="contained" color="primary" loading={paperqaLoading} disabled={isSubmitDisabled}>
                                        Submit
                                    </Button>
                                </Box>
                            </Stack>
                        </FormContainer>
                    </Box>
                ) : (
                    // Follow-up composer: Follow Up Question, Submit
                    <Box sx={{ maxWidth: 800, width: '100%', p: 3 }}>
                        <form onSubmit={handleFollowUpSubmit}>
                            <Stack direction="column" spacing={1.5}>
                                <TextField
                                    name="followup"
                                    label="Ask a follow-up question"
                                    value={followUpQuestion}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFollowUpQuestion(e.target.value)}
                                    fullWidth
                                    autoComplete="off"
                                    sx={{ bgcolor: '#0A3235', '& .MuiInputBase-input': { color: '#FFFFFF' }, '& .MuiInputLabel-root': { color: '#0FCB8C' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#0FCB8C55' } }}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <Button type="submit" variant="contained" color="primary" loading={paperqaLoading} disabled={!followUpQuestion || paperqaLoading}>
                                        Submit
                                    </Button>
                                </Box>
                            </Stack>
                        </form>
                    </Box>
                )}
            </ComposerBar>
            {/* Screen reader announcements for loading state */}
            <SrOnly aria-live="polite" aria-atomic="true" role="status">{loadingAnnouncement}</SrOnly>
            {/* Screen reader announcements for paper search */}
            <SrOnly aria-live="polite" aria-atomic="true" role="status">{searchAnnouncement}</SrOnly>
        </ChatContainer>
    );
};

/**
 * The definition below creates a component that we can use in the render
 * function above that have extended / customized CSS attached to them.
 * Learn more about mui styled:
 * @see https://mui.com/system/styled/
 *
 *
 * CSS is used to modify the display of HTML elements. If you're not familiar
 * with it here's quick introduction:
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS
 */
const ChatContainer = styled('div')`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: #032629;
    color: #FFFFFF;
`;

const ChatContent = styled('div')`
    flex: 1;
    overflow: auto;
    padding: 16px;
    padding-bottom: 96px; /* keep space for composer */
`;

const ComposerBar = styled('div')`
    position: sticky;
    bottom: 0;
    background: #0A3235;
    border-top: 1px solid rgba(255,255,255,0.12);
    padding: 12px 16px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const RightRow = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin: 8px 0;
`;

const LeftRow = styled('div')`
    display: flex;
    justify-content: flex-start;
    margin: 8px 0;
`;

const BubblePaper = styled(Paper)`
    max-width: 75%;
    padding: 12px 16px;
`;

const LoadingContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    width: 100%;
`;

// Visually hidden element for screen reader announcements
const SrOnly = styled('div')`
    border: 0 !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    -webkit-clip-path: inset(50%) !important;
    clip-path: inset(50%) !important;
    height: 1px !important;
    margin: -1px !important;
    overflow: hidden !important;
    padding: 0 !important;
    position: absolute !important;
    width: 1px !important;
    white-space: nowrap !important;
`;

/**
 * Makes button not full width
 */
const Button = styled(LoadingButton)`
    width: 130px;
    &:hover {
        background-color: #0A9B72;
    }
`;
